import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageSquareText, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ChatThread } from '../pages/admin/AdminCaAgent';

/**
 * The CA agent's floating chat (Business Rule #55, owner ask 2026-10-04: "a hover chat button which opens
 * up on click, on mobile and desktop"). Rendered only for an admin account: a round button bottom-right with
 * the count of what waits on the admin; a click opens the chat panel — full-screen on a phone, a 400 × 600
 * card on a desktop — the SAME thread the Bookkeeping → CA agent tab shows (`ChatThread`). Polls the badge
 * every 60 s while mounted; nothing renders for customers or staff.
 *
 * The button sits 88px up to clear the BottomNav; where the nav is hidden (`/admin`) it drops to 16px so it
 * doesn't float over the right-hand ₹ column of the Sales page on a phone.
 *
 * @param {{ navHidden?: boolean }} props
 */
const API = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/ca-agent`;
const POLL_MS = 60000;

export default function CaAgentChat({ navHidden = false }) {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [badge, setBadge] = useState(0);
  const alive = useRef(true);
  const isAdmin = !!user?.is_admin && !!token;

  const pull = useCallback(() => {
    if (!isAdmin) return;
    axios.get(`${API()}/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive.current) setBadge((r.data?.asking || 0) + (r.data?.unread || 0)); })
      .catch(() => {});
  }, [isAdmin, token]);

  useEffect(() => {
    alive.current = true;
    pull();
    const t = setInterval(pull, POLL_MS);
    return () => { alive.current = false; clearInterval(t); };
  }, [pull]);

  if (!isAdmin) return null;
  return (
    <>
      {!open && (
        <button type="button" onClick={() => setOpen(true)} aria-label="Open the CA agent chat" data-testid="ca-chat-fab"
                className="fixed right-4 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition"
                style={{ bottom: `calc(env(safe-area-inset-bottom) + ${navHidden ? 16 : 88}px)` }}>
          <MessageSquareText className="h-6 w-6" strokeWidth={1.75} />
          {badge > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-red-500 text-white text-[11px] font-semibold px-1.5 flex items-center justify-center" data-testid="ca-chat-badge">{badge > 99 ? '99+' : badge}</span>}
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 sm:inset-auto sm:right-4 sm:bottom-4 sm:w-[400px] sm:h-[600px] sm:max-h-[85vh] bg-white sm:rounded-2xl sm:border shadow-2xl flex flex-col" data-testid="ca-chat-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}>
            <div>
              <div className="font-semibold text-[15px]">Accounts</div>
              <div className="text-xs text-muted-foreground">your Senior Accounts Head · reports to you</div>
            </div>
            <button type="button" onClick={() => { setOpen(false); pull(); }} aria-label="Close" className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-muted" data-testid="ca-chat-close">
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <ChatThread token={token} fill onActivity={pull} />
          </div>
        </div>
      )}
    </>
  );
}
